import React, { useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

/** Route prefixes the assistant links into; these open in-app instead of in a new tab. */
const IN_APP_PREFIXES = ["/jobs", "/customers", "/consultants", "/contracts", "/time-reports"];

interface AiMarkdownProps {
	children: string;
	className?: string;
}

/**
 * Renders assistant markdown (GFM tables, lists, links). Links that point
 * into the dashboard — whichever host the backend's DASHBOARD_URL names —
 * navigate with the router so the user stays in the app; everything else
 * opens in a new tab.
 */
const AiMarkdown: React.FC<AiMarkdownProps> = ({ children, className }) => {
	const navigate = useNavigate();

	const handleLinkClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>, href?: string) => {
			if (!href) return;
			const target = toInAppPath(href);
			if (!target) return;
			// Let modifier clicks open a new tab as usual.
			if (event.metaKey || event.ctrlKey || event.shiftKey) return;
			event.preventDefault();
			navigate(target);
		},
		[navigate]
	);

	return (
		<div className={cn("ai-markdown text-sm leading-relaxed text-foreground", className)}>
			<ReactMarkdown
				remarkPlugins={[remarkGfm]}
				components={{
					a: ({ href, children: linkChildren }) => (
						<a
							href={href}
							target={toInAppPath(href) ? undefined : "_blank"}
							rel="noreferrer"
							onClick={(event) => handleLinkClick(event, href)}
							className="font-medium text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"
							{...toEntityMarker(href)}
						>
							{linkChildren}
						</a>
					),
					p: ({ children: c }) => <p className="mb-2 last:mb-0">{c}</p>,
					ul: ({ children: c }) => <ul className="mb-2 list-disc space-y-0.5 pl-5">{c}</ul>,
					ol: ({ children: c }) => <ol className="mb-2 list-decimal space-y-0.5 pl-5">{c}</ol>,
					li: ({ children: c }) => <li>{c}</li>,
					h1: ({ children: c }) => <h3 className="mb-1 mt-3 text-base font-semibold">{c}</h3>,
					h2: ({ children: c }) => <h3 className="mb-1 mt-3 text-base font-semibold">{c}</h3>,
					h3: ({ children: c }) => <h4 className="mb-1 mt-2 text-sm font-semibold">{c}</h4>,
					strong: ({ children: c }) => <strong className="font-semibold">{c}</strong>,
					code: ({ children: c, className: codeClassName }) =>
						codeClassName ? (
							<code className="block overflow-x-auto rounded-md bg-neutral-100 p-2 text-xs">
								{c}
							</code>
						) : (
							<code className="rounded bg-neutral-100 px-1 py-0.5 text-xs">{c}</code>
						),
					pre: ({ children: c }) => <pre className="mb-2">{c}</pre>,
					table: ({ children: c }) => (
						<div className="mb-2 overflow-x-auto rounded-md border border-border">
							<table className="w-full border-collapse text-xs">{c}</table>
						</div>
					),
					thead: ({ children: c }) => <thead className="bg-neutral-50">{c}</thead>,
					th: ({ children: c }) => (
						<th className="border-b border-border px-2 py-1.5 text-left font-semibold">{c}</th>
					),
					td: ({ children: c }) => (
						<td className="border-b border-border px-2 py-1.5 align-top last:border-b-0">{c}</td>
					),
					blockquote: ({ children: c }) => (
						<blockquote className="mb-2 border-l-2 border-border pl-3 text-muted-foreground">
							{c}
						</blockquote>
					),
					hr: () => <hr className="my-3 border-border" />,
				}}
			>
				{children}
			</ReactMarkdown>
		</div>
	);
};

/**
 * Path + search to navigate to when `href` points into the dashboard,
 * otherwise undefined. Absolute URLs on any host are accepted as long as the
 * path is one of ours, so links built for the test dashboard also work on
 * localhost.
 */
export function toInAppPath(href?: string): string | undefined {
	if (!href) return undefined;
	try {
		const url = new URL(href, window.location.origin);
		if (
			!IN_APP_PREFIXES.some(
				(prefix) =>
					url.pathname === prefix ||
					url.pathname.startsWith(prefix + "/") ||
					url.pathname.startsWith(prefix + "?")
			)
		) {
			return undefined;
		}
		return url.pathname + url.search;
	} catch {
		return undefined;
	}
}

/** Path segments under /customers, /consultants and /jobs that are pages, not entity ids. */
const NON_ENTITY_SEGMENTS = ["new", "create", "users", "corporate-groups"];

/** `data-*` attribute → route prefix the entity id follows. */
const ENTITY_MARKERS: Array<[attribute: string, prefix: string]> = [
	["data-customer", "/customers/"],
	["data-contact", "/consultants/"],
	["data-job", "/jobs/"],
];

/**
 * The context-menu marker for a link into a customer, consultant or job page,
 * so right-clicking the link opens the same global menu as a list row
 * (see ContactContextMenu). Empty for every other link.
 */
export function toEntityMarker(href?: string): Record<string, string> {
	const path = toInAppPath(href);
	if (!path) return {};

	for (const [attribute, prefix] of ENTITY_MARKERS) {
		if (!path.startsWith(prefix)) continue;
		const id = path.substring(prefix.length).split(/[/?#]/)[0];
		if (id && NON_ENTITY_SEGMENTS.indexOf(id) === -1) return { [attribute]: id };
	}

	return {};
}

export default AiMarkdown;
